import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Download, ExternalLink, Trash2, X, FileText, Image as ImageIcon, FolderOpen, FileUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "./PageHeader";
import StatusBadge from "./StatusBadge";
import { useAuth } from "../context/AuthContext";

const IMAGE_TYPES = ["PNG", "JPG", "JPEG", "WEBP", "GIF"];

function Viewer({ docType, row, canDownload, onClose, onDownload }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  const ft = (row.fileType || "").toUpperCase();
  const isPdf = ft === "PDF";
  const isImage = IMAGE_TYPES.includes(ft);
  const previewable = isPdf || isImage;
  useEffect(() => {
    if (!previewable) return undefined;
    let obj = null, cancelled = false;
    api.get(`/documents/${docType}/${row.id}/file`, { params: { mode: "view" }, responseType: "blob" })
      .then(({ data }) => { if (!cancelled) { obj = URL.createObjectURL(data); setUrl(obj); } })
      .catch((e) => setErr(errMsg(e)));
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [previewable, docType, row.id]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="doc-viewer-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="font-head font-bold text-slate-900 truncate">{row.filename}</div>
            <div className="text-xs text-slate-400">{row.oaNumber || row.customerName || ""} · {row.fileType}{row.size ? ` · ${Math.round(row.size / 1024)} KB` : ""}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canDownload && <button className="btn-outline btn-sm" onClick={() => onDownload(row)} data-testid="doc-viewer-download-btn"><Download className="h-4 w-4" /> Download Original</button>}
            <button className="p-1.5 rounded hover:bg-slate-100" onClick={onClose} data-testid="doc-viewer-close-btn"><X className="h-5 w-5 text-slate-600" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-slate-50 min-h-[300px] flex items-center justify-center">
          {previewable && url && isPdf && <iframe src={url} title={row.filename} className="w-full h-[70vh] rounded border border-slate-200 bg-white" />}
          {previewable && url && isImage && <img src={url} alt={row.filename} className="max-h-[70vh] max-w-full object-contain rounded border border-slate-200 bg-white" />}
          {previewable && !url && !err && <div className="text-sm text-slate-400">Loading original…</div>}
          {err && <div className="text-sm text-red-600">{err}</div>}
          {!previewable && (
            <div className="text-center space-y-3 py-10">
              <FileText className="h-12 w-12 text-slate-300 mx-auto" />
              <div className="text-sm text-slate-500" data-testid="doc-viewer-no-preview">Preview not available for this file type.</div>
              {canDownload && <button className="btn-primary btn-sm" onClick={() => onDownload(row)} data-testid="doc-viewer-download-original-btn"><Download className="h-4 w-4" /> Download Original</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DocumentLibrary({ docType, title, subtitle, metaFields = [], accept, columns, convert }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const can = (p) => isAdmin || perms.includes(p);
  const canUpload = can(`upload_${docType}`);
  const canView = can(`view_${docType}`);
  const canDownload = can(`download_${docType}`);
  const canDelete = can(`delete_${docType}`);
  const canPrint = docType === "quotation" && can("print_quotation");
  const canConvert = docType === "quotation" && can("create_oa");

  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ search: "", customer: "", oaNumber: "", fileType: "", dateFrom: "", dateTo: "" });
  const [viewer, setViewer] = useState(null);
  const [modal, setModal] = useState(false);
  const [meta, setMeta] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get(`/documents/${docType}`, { params });
    setRows(data);
  }, [docType, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const open = (row, download) => {
    api.get(`/documents/${docType}/${row.id}/file`, { params: { mode: download ? "download" : "view" }, responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        if (download) { const a = document.createElement("a"); a.href = url; a.download = row.filename; a.click(); }
        else { window.open(url, "_blank"); }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }).catch((e) => toast.error(errMsg(e)));
  };
  const printDoc = (row) => {
    api.get(`/documents/${docType}/${row.id}/file`, { params: { mode: "view" }, responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); const w = window.open(url); if (w) w.onload = () => w.print(); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const upload = async () => {
    if (!file) return toast.error("Please choose a file.");
    for (const f of metaFields) if (f.required && !meta[f.key]) return toast.error(`${f.label} is required.`);
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/documents/${docType}/upload`, fd, {
        params: { oaNumber: meta.oaNumber || "", customerName: meta.customerName || "", meta: JSON.stringify(meta) },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded"); setModal(false); setMeta({}); setFile(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };
  const del = async (row) => {
    if (!window.confirm(`Delete ${row.filename}?`)) return;
    try { await api.delete(`/documents/${docType}/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const convertToOA = async (row) => {
    try {
      const { data } = await api.post(`/quotations/${row.id}/to-oa`);
      toast.success(`Created draft ${data.oaNumber} — add products`);
      navigate(`/oa/${data.id}/edit`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle || `${rows.length} file(s)`}
        actions={canUpload && <button className="btn-accent" onClick={() => setModal(true)} data-testid={`upload-${docType}-btn`}><Upload className="h-4 w-4" /> Upload {title}</button>} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="card p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <input className="input" placeholder="Search file / OA / customer" value={filters.search} onChange={(e) => setF("search", e.target.value)} data-testid={`${docType}-search`} />
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => setF("customer", e.target.value)} />
          <input className="input" placeholder="OA Number" value={filters.oaNumber} onChange={(e) => setF("oaNumber", e.target.value)} />
          <input className="input" placeholder="File Type (PDF, XLSX...)" value={filters.fileType} onChange={(e) => setF("fileType", e.target.value)} />
          <input type="date" className="input" value={filters.dateFrom} onChange={(e) => setF("dateFrom", e.target.value)} />
          <input type="date" className="input" value={filters.dateTo} onChange={(e) => setF("dateTo", e.target.value)} />
        </div>

        <div className="card overflow-hidden"><div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {columns.map((c) => <th key={c} className="th">{c}</th>)}
            <th className="th">Uploaded By</th><th className="th">Upload Date</th><th className="th">Status</th><th className="th">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={columns.length + 4}><div className="flex items-center gap-2 py-6 justify-center"><FolderOpen className="h-5 w-5" /> No files uploaded yet.</div></td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`${docType}-row-${r.id}`}>
                {columns.map((c) => <td key={c} className="td">{
                  c === "OA Number" ? (r.oaNumber || "—") :
                  c === "Customer" ? (r.customerName || "—") :
                  c === "File Name" ? <span className="font-medium text-slate-800">{r.filename}</span> :
                  c === "File Type" ? r.fileType :
                  c === "File Size" ? (r.size ? `${Math.round(r.size / 1024)} KB` : "—") :
                  c === "Quotation Number" ? (r.meta?.quotationNumber || "—") :
                  c === "Quotation Date" ? (r.meta?.quotationDate || "—") :
                  c === "Bill Number" ? (r.meta?.billNumber || "—") :
                  c === "Bill Date" ? (r.meta?.billDate || "—") :
                  c === "Bill Amount" ? (r.billAmount != null ? inr(r.billAmount) : "—") :
                  c === "OA Net" ? (r.oaNet != null ? inr(r.oaNet) : "—") :
                  c === "Match" ? (
                    r.billAmount == null || r.oaNet == null ? <span className="text-slate-400 text-xs">—</span> :
                    r.mismatch ? <span className="badge bg-red-50 text-red-700 border border-red-200" data-testid={`bill-mismatch-${r.id}`}><AlertTriangle className="h-3 w-3 mr-1" /> Mismatch</span> :
                    <span className="badge bg-green-50 text-green-700 border border-green-200" data-testid={`bill-match-${r.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Matches</span>
                  ) : "—"
                }</td>)}
                <td className="td">{r.uploadedByName || r.uploadedBy}</td>
                <td className="td text-xs text-slate-400">{(r.uploadDate || "").slice(0, 10)}</td>
                <td className="td"><StatusBadge status={r.status} /></td>
                <td className="td"><div className="flex items-center gap-1">
                  {canView && <button title="View" className="p-1.5 rounded hover:bg-slate-100" onClick={() => setViewer(r)} data-testid={`${docType}-view-${r.id}`}><ExternalLink className="h-4 w-4 text-slate-600" /></button>}
                  {canDownload && <button title="Download" className="p-1.5 rounded hover:bg-slate-100" onClick={() => open(r, true)} data-testid={`${docType}-download-${r.id}`}><Download className="h-4 w-4 text-slate-600" /></button>}
                  {canPrint && <button title="Print" className="p-1.5 rounded hover:bg-slate-100" onClick={() => printDoc(r)} data-testid={`${docType}-print-${r.id}`}><FileText className="h-4 w-4 text-slate-600" /></button>}
                  {canConvert && <button title="Convert to OA" className="p-1.5 rounded hover:bg-amber-50" onClick={() => convertToOA(r)} data-testid={`quotation-convert-${r.id}`}><FileUp className="h-4 w-4 text-amber-600" /></button>}
                  {canDelete && <button title="Delete" className="p-1.5 rounded hover:bg-red-50" onClick={() => del(r)} data-testid={`${docType}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div></div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${docType}-upload-modal`}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-xl font-bold">Upload {title}</h3><button onClick={() => setModal(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="grid md:grid-cols-2 gap-4">
              {metaFields.map((f) => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="label">{f.label}{f.required ? " *" : ""}</label>
                  <input type={f.type || "text"} className="input" value={meta[f.key] || ""} onChange={(e) => setMeta((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={`${docType}-meta-${f.key}`} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="label">Original File *</label>
              <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-amber-400"
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }} data-testid={`${docType}-dropzone`}>
                <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-sm text-slate-600">{file ? file.name : "Drag & drop or click to select a file"}</div>
              </div>
              <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid={`${docType}-file-input`} />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={upload} disabled={uploading} data-testid={`${docType}-upload-submit`}>{uploading ? "Uploading…" : "Upload"}</button>
            </div>
          </div>
        </div>
      )}
      {viewer && <Viewer docType={docType} row={viewer} canDownload={canDownload} onClose={() => setViewer(null)} onDownload={(r) => open(r, true)} />}
    </div>
  );
}
